import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import BookModal from "./BookModal";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png"; // Update with your logo path

function BookLoan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [filteredLoanedBooksList, setFilteredLoanedBooksList] = useState([]); // For search

  const [searchTerm, setSearchTerm] = useState("");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      createdBy: localStorage.getItem("userId"),
      libraryMemberId: "",
      bookId: "",
      loanDays: "",
      loanDate: new Date().toISOString().split("T")[0],
      dueDate: "",
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [memberType, setMemberType] = useState(null);
  const [centersData, setCentersData] = useState([]);
  const [librariesData, setLibrariesData] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const [booksList, setBooksList] = useState([]);
  const [loanedBooksList, setLoanedBooksList] = useState([]);
  const [libraryId, setLibraryId] = useState(null);

  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [stockInfo, setStockInfo] = useState();

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCenters();
  }, [organizationId]);

  const fetchLoanedBooks = async () => {
    try {
      const response = await get(
        `LMS/GetBookLoanByCenterIdAndUserTypeAsync?centerId=${centerId}&userType=${memberType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLoanedBooksList(response?.bookLoanList || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  useEffect(() => {
    const filtered = loanedBooksList.filter((book) => {
      const searchLower = searchTerm ? searchTerm.toLowerCase() : "";
      return (
        (book.bookName && book.bookName.toLowerCase().includes(searchLower)) ||
        (book.memberName && book.memberName.toLowerCase().includes(searchLower))
      );
    });
    setFilteredLoanedBooksList(filtered);
  }, [searchTerm, loanedBooksList]);

  useEffect(() => {
    if (memberType && centerId) {
      fetchLoanedBooks();
    }
  }, [memberType, centerId, triggerRerender]);

  const getMembersByTypeAndCenter = async () => {
    if (!token || !memberType || !centerId) {
      console.error("Missing required parameters or unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetLibraryMembersByCenterIdAndLibraryIdAsync?centerId=${centerId}&userType=${memberType}&libraryId=${libraryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMembersData(response?.libraryMemberService || []);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    if (memberType && centerId && libraryId) {
      getMembersByTypeAndCenter();
    }
  }, [memberType, centerId, libraryId]);

  const getLibrariesListByCenterId = async (centerId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetLibraryByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLibrariesData(response?.libraryService);
    } catch (error) {
      console.error("Error fetching libraries:", error);
    }
  };

  useEffect(() => {
    if (centerId) {
      getLibrariesListByCenterId(centerId);
    }
  }, [centerId]);

  const getBooksListByLibraryId = async (libraryId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetBooksBylibraryIdAsync?libraryId=${libraryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBooksList(response?.bookService || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

  const loanDays = watch("loanDays");

  useEffect(() => {
    try {
      if (loanDays) {
        const today = new Date();
        const dueDate = new Date(
          today.setDate(today.getDate() + parseInt(loanDays, 10))
        );
        setValue("dueDate", dueDate.toISOString().split("T")[0]);
      }
    } catch (error) {}
  }, [loanDays, setValue]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "bookName",
        header: "Book",
      },
      {
        accessorKey: "memberName",
        header: "Member",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div>
            <button
              type="button"
              className="btn btn-outline-primary py-1 px-2"
              onClick={() => handleBookReturnClick(row.original)}
            >
              Return Book
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const dataToExport = filteredLoanedBooksList.map((row, index) => ({
      "Sr No": index + 1,
      Book: row.bookName,
      Member: row.memberName,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loaned Books");
    XLSX.writeFile(workbook, "LoanedBooks.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30; // Adjust logo width as needed
    const logoHeight = 8; // Adjust logo height as needed
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    
    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Loaned Books List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = filteredLoanedBooksList.map((row, index) => {
        return [
          index + 1,
          row.bookName,
          row.memberName,
        ];
      });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      startY: titleY + 10,
      theme: "striped",
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("LoanedBooksList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const checkBookStock = async (bookId) => {
    try {
      const response = await get(
        `LMS/GetBooksStockByIdAsync?bookId=${bookId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        setStockInfo(response?.bookService?.stockQty);
      }
    } catch (error) {
      console.error("Failed to fetch book qty:", error);
    }
  };

  const saveData = async (data) => {
    if (!token || !stockInfo) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      bookId: data?.bookId?.value,
      libraryMemberId: data?.libraryMemberId?.value,
    };

    try {
      const response = await post("LMS/AddUpdateBookLoanAsync", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        setStockInfo();
        reset({
          id: 0,
          createdBy: localStorage.getItem("userId"),
          libraryMemberId: "",
          bookId: "",
          loanDays: "",
          loanDate: new Date().toISOString().split("T")[0],
          dueDate: "",
          centerId: getValues("centerId"),
          libraryId: "",
          memberType: "",
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleBookReturnClick = async (row) => {
    try {
      const response = await get(
        `LMS/GetBookLoanByIDAsync?bookLoanId=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setModalData(response?.bookLoanList);
        setShowModal(true);
      } else {
        console.error("Error fetching details");
      }
    } catch (error) {
      console.error("Error fetching", error);
    }
  };

  const submitBookReturn = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post(
        `LMS/ReturnBookLoanUpdateByIDAsync`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setShowModal(false);
        setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      console.error("Error saving book return:", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Book Loan</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Center<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`centerId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`centerId`, null);
                            setValue("libraryId", null); // Clear library
                            setValue("libraryMemberId", null); // Clear member
                            setValue("bookId", null); // Clear book
                            setCenterId(null);
                            setLibrariesData([]); // Reset libraries
                            setMembersData([]); // Reset members
                            setBooksList([]); // Reset books
                          } else {
                            setValue(
                              `centerId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            setCenterId(selectedOption.value);
                            setValue("libraryId", null); // Clear library
                            setValue("libraryMemberId", null); // Clear member
                            setValue("bookId", null); // Clear book
                            setLibrariesData([]); // Reset libraries
                            setMembersData([]); // Reset members
                            setBooksList([]); // Reset books
                            // trigger(["libraryId", "libraryMemberId", "bookId"]); // Validate cleared fields
                          }
                          trigger(`centerId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Member Type<span className="text-danger fs-9">*</span>
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        {...register("memberType", {
                          required: "Please select a member type.",
                        })}
                        type="radio"
                        id="studentType"
                        value="student"
                        className="form-check-input"
                        onChange={(e) => {
                          setMemberType(e.target.value);
                          setValue("libraryMemberId", null); // Clear dependent input
                          setMembersData([]); // Reset members data
                          // trigger("libraryMemberId"); // Validate cleared field
                        }}
                      />
                      <label className="form-check-label" htmlFor="studentType">
                        Student
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        {...register("memberType", {
                          required: "This field is required.",
                        })}
                        type="radio"
                        id="teacherType"
                        value="teacher"
                        className="form-check-input"
                        onChange={(e) => {
                          setMemberType(e.target.value);
                          setValue("libraryMemberId", null); // Clear dependent input
                          setMembersData([]); // Reset members data
                          // trigger("libraryMemberId"); // Validate cleared field
                        }}
                      />
                      <label className="form-check-label" htmlFor="teacherType">
                        Teacher
                      </label>
                    </div>
                  </div>
                  {errors.memberType && (
                    <small className="text-danger">
                      {errors.memberType.message}
                    </small>
                  )}
                </div>

                <div className="m-0">
                  <hr />
                </div>

                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Library<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`libraryId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={librariesData.map((library) => ({
                          value: library.id,
                          label: library.name,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`libraryId`, null);
                            setValue("bookId", null); // Clear book
                            setBooksList([]); // Reset books
                            setLibraryId(null);
                            // trigger(["libraryId", "bookId"]); // Validate cleared fields
                          } else {
                            setValue(
                              `libraryId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            setLibraryId(selectedOption.value);
                            getBooksListByLibraryId(selectedOption.value); // Fetch books for the selected library
                            setValue("bookId", null); // Clear book
                            setBooksList([]); // Reset books
                            // trigger(["bookId"]); // Validate cleared fields
                          }
                          trigger(`libraryId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.libraryId && (
                    <small className="text-danger">
                      {errors.libraryId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label form-label-new d-flex align-items-center">
                    Book<span className="text-danger fs-9">*</span>
                    <span className="ms-auto">
                      {stockInfo > 0 ? (
                        <span className="text-success">
                          Stock Available ({stockInfo})
                        </span>
                      ) : stockInfo == 0 ? (
                        <span className="text-danger">Out of Stock</span>
                      ) : null}
                    </span>
                  </label>
                  <Controller
                    name={`bookId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={booksList.map((book) => ({
                          value: book.id,
                          label: book.title,
                        }))}
                        onChange={async (selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`bookId`, null);
                            setStockInfo(null);
                          } else {
                            setValue(
                              `bookId`,
                              selectedOption || { value: 0, label: "" }
                            );
                            checkBookStock(selectedOption.value);
                          }
                          trigger(`bookId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.bookId && (
                    <small className="text-danger">
                      {errors.bookId.message}
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Member<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name={`libraryMemberId`}
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={membersData?.map((member) => ({
                          value: member.libraryMemberId,
                          label: member.mamberName,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue(`libraryMemberId`, null);
                          } else {
                            setValue(
                              `libraryMemberId`,
                              selectedOption || { value: 0, label: "" }
                            );
                          }
                          trigger(`libraryMemberId`);
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        // isClearable
                      />
                    )}
                  />
                  {errors.libraryMemberId && (
                    <small className="text-danger">
                      {errors.libraryMemberId.message}
                    </small>
                  )}
                </div>

                <div className="m-0"></div>

                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Loan Days<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Loan Days"
                    {...register(`loanDays`, {
                      valueAsNumber: true,
                      required: "This field is required",
                      validate: (value) =>
                        value >= 0 || "Please enter correct value.",
                    })}
                    onKeyDown={(e) => {
                      if (
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "+" ||
                        e.key === "."
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value <= 0 ? 1 : e.target.value;
                    }}
                  />
                  {errors.loanDays && (
                    <small className="text-danger">
                      {errors.loanDays.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Loan Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`loanDate`, {
                      required: "This field is required.",
                    })}
                    type="date"
                    className="form-control"
                    disabled={true}
                  />

                  {errors.loanDate && (
                    <small className="text-danger">
                      {errors.loanDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Due Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`dueDate`, {
                      required: "This field is required.",
                    })}
                    type="date"
                    className="form-control"
                    placeholder=""
                    disabled={true}
                  />
                  {errors.dueDate && (
                    <small className="text-danger">
                      {errors.dueDate.message}
                    </small>
                  )}
                </div>

                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={
                      stockInfo === 0 || stockInfo === null || isSubmitting
                    }
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              

              <CustomDatatableGrid
                columns={columns}
                data={filteredLoanedBooksList}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <BookModal
          title="Return Book"
          onClose={() => setShowModal(false)}
          data={modalData}
          returnBook={submitBookReturn}
        />
      )}
    </>
  );
}

export default BookLoan;
